import clsx from 'clsx'
import React from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { DisclosableItemCardWithAddCartFormProps } from './type'

import {
  ItemOrderUnitFormField,
  ItemRelatedLinks,
  ItemCardDetailToggle,
  DisclosableItemCardHeader,
  DisclosableItemCardWrapper,
} from '@/components/features/item'
import { DeleteFilledIcon } from '@/components/icons/DeleteFilled'
import { Icon } from '@/components/ui'

export const DisclosableItemCardWithAddCartForm = ({
  item,
  control,
  itemIndex,
  disabled = false,
  isOpen,
  isOutsideModal = false,
  itemCardDetailToggleHeaderClassName = 'tw-bg-white tw-text-xs',
  toggle,
  register,
  onAfterChange,
  onClickRemoveFromCart,
}: DisclosableItemCardWithAddCartFormProps) => {
  const { t } = useTranslation()
  const salesTypeQuantities = useWatch({
    control,
    name: `items.${itemIndex}.salesTypeQuantities`,
  })
  const totalOrderCount = React.useMemo(
    () =>
      salesTypeQuantities?.reduce(
        (sum, current) => sum + (current.quantity || 0),
        0
      ),
    [salesTypeQuantities]
  )

  return (
    <DisclosableItemCardWrapper
      isOpen={isOpen}
      disabled={disabled}
      toggle={toggle}
      headingNode={
        <DisclosableItemCardHeader
          item={item}
          totalOrderCount={totalOrderCount}
          disabled={disabled}
        />
      }
      detailContentNode={
        <>
          <ItemRelatedLinks item={item} />
          <ItemCardDetailToggle
            item={item}
            isOutsideModal={isOutsideModal}
            headerClassName={itemCardDetailToggleHeaderClassName}
          />
          <div className={clsx('tw-mt-4', 'lg:tw-w-[560px]')}>
            <ItemOrderUnitFormField
              control={control}
              itemIndex={itemIndex}
              register={register}
              onAfterChange={onAfterChange}
            />
          </div>

          {onClickRemoveFromCart && (
            <div
              className={clsx(
                'tw-mt-4',
                'tw-flex tw-items-center tw-justify-center',
                'tw-px-2 tw-py-2',
                'tw-border-t tw-border-gray-300',
                'tw-text-center'
              )}
            >
              <button
                type="button"
                className={clsx(
                  'tw-inline-flex tw-items-center tw-gap-x-1 tw-text-alert-500 hover:tw-opacity-75'
                )}
                onClick={onClickRemoveFromCart}
              >
                <Icon icon={DeleteFilledIcon} size={'2xl'} />
                <span className={clsx('tw-text-sm')}>
                  {t('item.cart.remove')}
                </span>
              </button>
            </div>
          )}
        </>
      }
    />
  )
}
